import React, { useEffect } from 'react'
import { Stack, Container, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { athleteTrainingSchema } from '../validators'
import { RoutineType } from '../../../../domain/db/RoutineTypeDB'
import { WeekdaysDB } from '../../../../domain/db/WeekdaysDB'
import { NumericDB } from '../../../../domain/db/NumericDB'
import { Button } from '../../../../components/Button'
import api from '../../../../services/api'
import { TextArea } from '../../../../components/TextArea'
import { LoadingButton } from '@mui/lab'
import { FormInputText } from 'components/FormInputText'
import { FormInputSelect } from 'components/FormInputSelect'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { useAppDispatch } from 'redux/hooks'

type AthleteTrainingFormProps = {
  id: string
  name: string
  sequence: number
  note?: string
  professionalAthleteRoutineId?: string
}
export const AthleteTrainingForm = ({ isVisible, training, athleteRoutine, onSubmitted, onClose, loading }: any) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<AthleteTrainingFormProps>({
    defaultValues: { professionalAthleteRoutineId: athleteRoutine?.id }, resolver: yupResolver(athleteTrainingSchema)
  })

  useEffect(() => {
    if (isVisible) {
      reset()
    }
    dataFetch().catch(console.error)
  }, [isVisible])

  const dataFetch = async () => {
    if (training == null) return
    const fields = ['id', 'name', 'sequence', 'note']
    fields.forEach((field: any) => setValue(field, training[field]))
  }

  const getTypeData = () => {
    if (athleteRoutine?.type === RoutineType.WEEKDAYS) return WeekdaysDB()
    return NumericDB()
  }

  const getDefaultValue = () => {
    if (athleteRoutine?.type === RoutineType.WEEKDAYS) return 2
    return 1
  }

  const handleUpInsertTraining = async (athleteTraining: AthleteTrainingFormProps): Promise<void> => {
    try {
      let saveMessage = t('training.create_success')
      if (athleteTraining.id !== undefined) {
        saveMessage = t('training.update_success')
        await api.put(`/professional-athlete-trainings/${athleteTraining.id}`, athleteTraining)
      } else {
        athleteTraining.professionalAthleteRoutineId = athleteRoutine.id
        await api.post('/professional-athlete-trainings', athleteTraining)
      }
      onSubmitted()
      dispatch(showMessage({ message: saveMessage }))
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={isVisible}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle>{(training ? t('training.editTraining') : t('training.newTraining'))}</DialogTitle>
      <DialogContent>
        <Container component="main">
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="sequence"
              defaultValue={getDefaultValue()}
              render={({ field: { onChange, value } }) => (
                <FormInputSelect
                  name="sequence"
                  control={control}
                  value={String(value)}
                  onChange={onChange}
                  placeholder={`${t('general.validator.option')}`}
                  label={`${t('training.field.sequence.label')}`}
                  items={getTypeData()}
                  errorMessage={''}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="name"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  name="name"
                  control={control}
                  value={value}
                  placeholder={`${t('training.field.name.placeholder')}`}
                  label={`${t('training.field.name.label')}`}
                  onChange={onChange}
                  errorMessage={errors.name?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="note"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <TextArea
                  value={value}
                  label={`${t('training.field.note.label')}`}
                  placeholder={`${t('training.field.note.placeholder')}`}
                  onChange={onChange}
                />
              )} />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={loading} onClick={handleSubmit(handleUpInsertTraining)} variant="contained">
          {t('general.btn.save')}
        </LoadingButton>
        <Button title={t('general.btn.dismiss')} variant="contained" color="secondary" onClick={onClose} />
      </DialogActions>
    </Dialog>
  )
}
